import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PersonIcon from '@mui/icons-material/Person';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SearchIcon from '@mui/icons-material/Search';
import SpaRoundedIcon from '@mui/icons-material/SpaRounded';

import { useDashboardSteps, DashboardStep } from '../../../hooks/useDashboardSteps';
import styles from './DashboardSteps.module.scss';

interface DashboardStepsProps {
    type?: 'patient' | 'professional';
}

export const DashboardSteps: React.FC<DashboardStepsProps> = ({ type = 'professional' }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { steps, loading } = useDashboardSteps();

    if (loading) return null;
    if (steps.length === 0) return null;
    if (steps.every(s => s.status === 'completed')) return null;

    const handleAction = (step: DashboardStep) => {
        if (step.actionPath && step.status !== 'pending') {
            navigate(step.actionPath);
        }
    };

    const getStepIcon = (id: string, status: string) => {
        if (type === 'professional') {
            if (status === 'completed') return <span style={{ fontSize: '24px' }}>✓</span>;
            return <span style={{ fontSize: '24px' }}>○</span>;
        }

        if (status === 'completed') return <CheckRoundedIcon sx={{ fontSize: 22 }} />;
        switch (id) {
            case 'complete_profile': return <PersonIcon sx={{ fontSize: 22 }} />;
            case 'search_professionals': return <SearchIcon sx={{ fontSize: 22 }} />;
            default: return <SpaRoundedIcon sx={{ fontSize: 22 }} />;
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'completed': return t('dashboard.steps.status.completed', '¡Listo!');
            case 'current': return t('dashboard.steps.status.inProgress', 'Por configurar');
            default: return t('dashboard.steps.status.pending', 'Próximamente');
        }
    };

    return (
        <section className={styles.journey}>
            <div className={styles.journey__head}>
                <span className={styles.journey__mark}>✦</span>
                <div className={styles.journey__text}>
                    <h2>{t('dashboard.steps.title')}</h2>
                    <p>{type === 'professional' ? t('dashboard.steps.subtitle_professional', 'Completá los tres pasos para dejar tu perfil pronto. Cuando la carga requerida esté completa, tu perfil pasará automáticamente a revisión.') : t('dashboard.steps.subtitle')}</p>
                </div>
            </div>

            <div className={classNames(styles.steps, {
                [styles['steps--patient']]: type === 'patient'
            })}>
                {steps.map((step) => {
                    return (
                        <article
                            key={step.id}
                            className={classNames(styles.step, {
                                [styles['step--done']]: step.status === 'completed',
                                [styles['step--highlight']]: step.status === 'current',
                                [styles['step--disabled']]: step.status === 'pending'
                            })}
                            onClick={() => handleAction(step)}
                        >
                            <div className={styles.step__top}>
                                <div className={styles.step__ico}>
                                    {getStepIcon(step.id, step.status)}
                                </div>
                                <span className={classNames(styles.badge, {
                                    [styles['badge--done']]: step.status === 'completed',
                                    [styles['badge--progress']]: step.status === 'current',
                                    [styles['badge--off']]: step.status === 'pending'
                                })}>
                                    {getStatusLabel(step.status)}
                                </span>
                            </div>

                            <h3 className={styles.step__title}>{step.label}</h3>

                            <button
                                className={classNames(styles['step__btn'], {
                                    [styles['step__btn--primary']]: step.status !== 'pending',
                                    [styles['step__btn--disabled']]: step.status === 'pending'
                                })}
                                disabled={step.status === 'pending'}
                            >
                                {step.actionLabel || (step.status === 'completed'
                                    ? t('dashboard.steps.action.view', 'Completar')
                                    : t('dashboard.steps.action.complete', 'Comenzar'))
                                }
                                <span className={styles.step__arrow}>→</span>
                            </button>
                        </article>
                    );
                })}
            </div>
        </section>
    );
};